import _ from 'lodash'
import moment from 'moment'
import axiosInstance from './axiosInstance'
import * as rt from '../config/routeType'
import { isMaintenanceResponse } from '../utils/maintenance'
import { filterEnabledGeojson } from '../utils/geojsonHelpers'

const getEnabledOnlyOption = options => {
  if (typeof options === 'boolean') return options
  return options?.enabledOnly !== false
}

const serviceErrorData = error =>
  error?.response?.data || {
    action: 'error',
    ok: false,
    message: error?.message || 'Error de red',
  }

export const getAllLocations = async (canton = null, tenant = null, options = {}) => {
  const enabledOnly = getEnabledOnlyOption(options)
  const params = new URLSearchParams()
  if (canton) params.append('canton', canton)
  if (tenant) params.append('tenant', tenant)
  if (enabledOnly) params.append('habilitado', 'true')

  const queryString = params.toString()
  const url = queryString ? `${rt.FEATURE_LOCATION}?${queryString}` : rt.FEATURE_LOCATION

  const response = await axiosInstance.get(url)
  return filterEnabledGeojson(
    response.data.locations || { type: 'FeatureCollection', features: [] },
    enabledOnly,
  )
}

export const getAllDataByTreesFilters = async (
  type,
  filterlist,
  onlyIds = false,
  canton = null,
  tenant = null,
  options = {},
) => {
  const enabledOnly = getEnabledOnlyOption(options)
  const queryList = _.includes(['pseudonym', 'specie', 'family', 'site', 'sitetype', 'zonetype', 'code'], type)
    ? _.map(filterlist, 'title')
    : _.map(filterlist, 'id')

  const data = {
    type,
    queryList,
    onlyIds,
    ...(canton && { canton }),
    ...(tenant && { tenant }),
    ...(!onlyIds && enabledOnly && { habilitado: true }),
  }
  const response = await axiosInstance.post(rt.GEOJSON_FILTERS, data)

  return !onlyIds
    ? filterEnabledGeojson(
      response.data.response || { type: 'FeatureCollection', features: [] },
      enabledOnly,
    )
    : response.data.response
}

export const getPropertiesByID = async treeId => {
  let carbonData = null
  const response = await axiosInstance.get(`${rt.FEATURE_BY_ID}/${treeId}`)
  let properties = response.data.properties || {}

  // El endpoint /feature/:id responde 200 con un esqueleto (code y attributes en null)
  // cuando el árbol no existe. Tratarlo como "no encontrado" para que los consumidores
  // manejen el caso vía null en lugar de operar sobre datos nulos.
  if (properties.code == null && properties.attributes == null) return null

  const {
    id,
    code,
    codeMode,
    code_mode,
    prioridadAtencion,
    prioridad_atencion,
    attentionPriority,
    tenantId,
    tenant_id,
    referenciaUbicacion,
    referencia_ubicacion,
    locationReference,
  } = response.data || {}

  const resolvedCodeMode = codeMode ?? code_mode ?? null
  const resolvedPrioridadAtencion =
    prioridadAtencion ?? prioridad_atencion ?? attentionPriority ?? null
  const resolvedTenantId = tenantId ?? tenant_id ?? null
  const resolvedLocationReference =
    referencia_ubicacion ?? referenciaUbicacion ?? locationReference ?? null
  const geometryCoordinates = Array.isArray(response.data?.geometry?.coordinates)
    ? response.data.geometry.coordinates
    : null
  const geometryLng =
    geometryCoordinates && geometryCoordinates.length >= 2
      ? Number(geometryCoordinates[0])
      : null
  const geometryLat =
    geometryCoordinates && geometryCoordinates.length >= 2
      ? Number(geometryCoordinates[1])
      : null

  // Preservar campos de nivel raíz si no vienen dentro de properties
  if (properties.id == null && id != null) properties.id = id
  if (properties.code == null && code != null) properties.code = code
  if (properties.codeMode == null && resolvedCodeMode != null)
    properties.codeMode = resolvedCodeMode
  if (properties.prioridadAtencion == null && resolvedPrioridadAtencion != null)
    properties.prioridadAtencion = resolvedPrioridadAtencion
  if (properties.tenantId == null && resolvedTenantId != null)
    properties.tenantId = resolvedTenantId
  if (properties.referencia_ubicacion == null && resolvedLocationReference != null)
    properties.referencia_ubicacion = resolvedLocationReference
  if (properties.locationReference == null && resolvedLocationReference != null)
    properties.locationReference = resolvedLocationReference
  if (
    (properties.longX == null || properties.longX === '') &&
    Number.isFinite(geometryLng) &&
    geometryLng >= -180 &&
    geometryLng <= 180
  ) {
    properties.longX = geometryLng
  }
  if (
    (properties.latY == null || properties.latY === '') &&
    Number.isFinite(geometryLat) &&
    geometryLat >= -90 &&
    geometryLat <= 90
  ) {
    properties.latY = geometryLat
  }

  try {
    carbonData = await axiosInstance.get(`${rt.CARBON_CALCULE_BY_ID}/${treeId}`)
  } catch (carbonErr) {
    console.warn(`${carbonErr.response?.data?.error}:`, carbonErr.response?.data?.message)
  }

  properties.carbon = {
    value: carbonData?.data?.biomasa_estimado || null,
    unit: carbonData?.data?.formula?.unidad || null,
  }

  const inconv_names = _.map(properties.inconveniences, 'name')
  const uniqCategorys = _.uniq(inconv_names)

  const inconvByCategory = uniqCategorys.map(catName => {
    let newObj = { id: null, category: catName, attributes: [] }

    properties.inconveniences.forEach(inconv_ => {
      let attributes = {}
      if (inconv_.name === catName) newObj.id = inconv_.id
      if (inconv_.name === catName) {
        attributes[`${inconv_.type.key}`] = {
          id: inconv_.type.id,
          label: inconv_.type.description,
          value: inconv_.type.value,
          date: inconv_.dateLog,
        }
        newObj.attributes.push(attributes)
      }
    })
    return newObj
  })
  properties.inconveniences = inconvByCategory
  return properties
}

export const getPopupDataByID = async treeId => {
  const response = await axiosInstance.get(`${rt.FEATURE_POPUP_BY_ID}/${treeId}`)
  let properties = response.data.properties || {}

  const {
    id,
    code,
    tenantId,
    tenant_id,
    referenciaUbicacion,
    referencia_ubicacion,
    locationReference,
  } = response.data || {}

  const resolvedTenantId = tenantId ?? tenant_id ?? null
  const resolvedLocationReference =
    referencia_ubicacion ?? referenciaUbicacion ?? locationReference ?? null

  if (properties.id == null && id != null) properties.id = id
  if (properties.code == null && code != null) properties.code = code
  if (properties.tenantId == null && resolvedTenantId != null)
    properties.tenantId = resolvedTenantId
  if (properties.referencia_ubicacion == null && resolvedLocationReference != null)
    properties.referencia_ubicacion = resolvedLocationReference
  if (properties.locationReference == null && resolvedLocationReference != null)
    properties.locationReference = resolvedLocationReference

  const inconv_names = _.map(properties.inconveniences, 'name')
  const uniqCategorys = _.uniq(inconv_names)

  const inconvByCategory = uniqCategorys.map(catName => {
    let newObj = { id: null, category: catName, attributes: [] }
    properties.inconveniences.forEach(inconv_ => {
      let attributes = {}
      if (inconv_.name === catName) newObj.id = inconv_.id
      if (inconv_.name === catName) {
        attributes[`${inconv_.type.key}`] = {
          id: inconv_.type.id,
          label: inconv_.type.description,
          value: inconv_.type.value,
          date: inconv_.dateLog,
        }
        newObj.attributes.push(attributes)
      }
    })
    return newObj
  })
  properties.inconveniences = inconvByCategory

  return properties
}

// Obtener feature completo (properties + geometry)
export const getCompleteFeatureByID = async treeId => {
  try {
    const response = await axiosInstance.get(`${rt.FEATURE_BY_ID}/${treeId}`)
    const feature = response.data || {}

    // Obtener properties procesadas (con carbon e inconveniences formateados)
    const properties = await getPropertiesByID(treeId)

    // Retornar feature completo con properties procesadas
    return {
      ...feature,
      properties,
    }
  } catch (err) {
    console.error('Error getting complete feature:', err)
    throw err
  }
}

export const login = ({ email, password }) => {
  return axiosInstance
    .post(rt.AUTH_LOGIN, {
      email: email,
      contrasena: password,
    })
    .then(res => res.data)
    .catch(err => {
      console.error('Login error:', err?.response?.data || err.message)
      return err.response.data
    })
}

function createOptionList(res, key, value) {
  let data = {}
  res.data?.res?.forEach(o => {
    let ObjNew = {}
    if (Boolean(o.habilitado?.data?.[0])) {
      ObjNew[`${o[key]}`] = o[value]
      data = { ...data, ...ObjNew }
    }
  })
  return data
}

const resolveEnabledValue = value => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (_.includes(['1', 'true', 't', 'si', 'sí', 'yes'], normalized)) return true
    if (_.includes(['0', 'false', 'f', 'no'], normalized)) return false
  }
  if (Array.isArray(value)) return Boolean(value[0])
  if (value && typeof value === 'object') {
    if (Array.isArray(value.data)) return Boolean(value.data[0])
    if (value.value !== undefined) return resolveEnabledValue(value.value)
  }
  return Boolean(value)
}

const getFamilyRows = payload =>
  payload?.families || payload?.familias || payload?.family || payload?.res || []

const normalizeValidationStatus = value => {
  if (!value) return null
  const normalized = String(value).trim().toLowerCase()
  if (normalized === 'verificado') return 'verificado'
  if (normalized === 'pendiente') return 'pendiente'
  return normalized
}

const normalizeFamily = row => ({
  id: row?.id ?? row?.id_f ?? row?.idFamilia ?? row?.id_familia,
  nombre: row?.nombre ?? row?.nombreFamilia ?? row?.nombre_familia ?? row?.name ?? '',
  habilitado: resolveEnabledValue(row?.habilitado ?? row?.enabled ?? row?.activo),
  estado_validacion: normalizeValidationStatus(row?.estado_validacion),
})

export const getPseudonymAll = () => {
  return axiosInstance.get(rt.PSEUDONYM).then(res => {
    const { pseudonym } = res.data
    let response = []
    pseudonym?.forEach(obj => {
      if (Boolean(obj.habilitado.data[0]))
        response.push({ id: obj.id_ps, name: obj.nombre_comun })
    })
    const unique = _.uniqBy(response, 'name')
    const orderby = _.orderBy(unique, ['name'], ['asc'])
    return orderby
  })
  // .catch(err => err.response.data)
}

export const getPseudonymsBySpecieId = specieId => {
  return axiosInstance
    .get(`${rt.PSEUDONYM_BY_SPECIE}/${specieId}`)
    .then(res => {
      const { pseudonym } = res.data
      let response = []
      pseudonym?.forEach(obj => {
        if (Boolean(obj.habilitado.data[0]))
          response.push({ id: obj.id_ps, name: obj.nombre_comun })
      })
      const unique = _.uniqBy(response, 'name')
      const orderby = _.orderBy(unique, ['name'], ['asc'])
      return orderby
    })
    .catch(err => err.response.data)
}

export const getSitesDataByName = (siteName, field) => {
  return axiosInstance
    .get(`${rt.SITE_BY_NAME}/${siteName}`)
    .then(res => {
      const { sites } = res.data
      if (!sites?.length) return []

      // Flatten nested tenant/section data from normalized structure
      const labels = []
      sites.forEach(site => {
        const tenants = site.tenants || []
        if (field === 'acronimoSitio') {
          tenants.forEach(t => {
            if (t.acronimoSitio) labels.push(t.acronimoSitio)
          })
        } else if (field === 'seccion') {
          tenants.forEach(t => {
            (t.secciones || []).forEach(s => {
              if (s.codigo) labels.push({ codigo: s.codigo, nombre: s.nombre || s.codigo })
            })
          })
        }
      })

      if (field === 'seccion') {
        const seen = new Set()
        return labels.filter(s => {
          if (seen.has(s.codigo)) return false
          seen.add(s.codigo)
          return true
        }).map((s, index) => ({ id: index, label: s.codigo, nombre: s.nombre }))
      }
      const unique = [...new Set(labels)]
      return unique.map((label, index) => ({ id: index, label }))
    })
    .catch(err => err.response?.data)
}

export const getFamilyAll = () => {
  return axiosInstance
    .get(rt.FAMILY)
    .then(res => {
      const rows = getFamilyRows(res?.data)
      const enabledFamilies = rows
        .map(normalizeFamily)
        .filter(row => row.id !== undefined && row.id !== null && row.nombre && row.habilitado)

      return enabledFamilies.reduce((acc, row) => {
        acc[`${row.id}`] = row.nombre
        return acc
      }, {})
    })
    .catch(err => err.response?.data)
}

export const getFamilyTableAll = () => {
  return axiosInstance
    .get(rt.FAMILY)
    .then(res => _.map(getFamilyRows(res?.data), normalizeFamily))
    .catch(err => err.response?.data)
}

export const getOptionsfamily = () => {
  return getFamilyTableAll()
    .then(response => {
      const listElemEnable = _.filter(response, ['habilitado', true])
      const uniq = _.unionBy(listElemEnable, 'nombre')
      const sorted = uniq.sort((a, b) =>
        a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'accent' })
      )
      return (
        sorted?.map(o => ({
          title: o.nombre,
          id: o.id,
        })) ?? []
      )
    })
    .catch(error => error)
}

export const updateFamilyTableById = (data, specieId) => {
  return axiosInstance
    .put(`${rt.FAMILY_UPDATE}/${specieId}`, data)
    .then(response => response.data)
    .catch(err => err.response?.data)
}

export const createFamily = (data, token) => {
  return axiosInstance({
    method: 'post',
    url: rt.FAMILY_CREATE,
    data,
  })
    .then(response => response.data)
    .catch(err => err.response?.data)
}

export const getFamilySpecieCount = familyId =>
  axiosInstance
    .get(`${rt.FAMILY_SPECIE_COUNT}/${familyId}`)
    .then(res => ({ count: res.data?.count ?? 0 }))
    .catch(err => ({ error: true, status: err.response?.status, message: err.response?.data?.message || 'No se pudo verificar especies asociadas.' }))

export const disableFamilyById = familyId =>
  axiosInstance
    .put(`${rt.FAMILY_DISABLE}/${familyId}`)
    .then(res => res.data)
    .catch(err => ({ ...(err.response?.data || {}), error: true, status: err.response?.status, message: err.response?.data?.message || 'No se pudo deshabilitar la familia.' }))

export const permanentlyDeleteFamilyById = familyId =>
  axiosInstance
    .delete(`${rt.FAMILY_DELETE}/${familyId}`)
    .then(res => res.data)
    .catch(err => ({ ...(err.response?.data || {}), error: true, status: err.response?.status, message: err.response?.data?.message || 'No se pudo eliminar la familia.' }))

export const getSpecieAll = () => {
  return axiosInstance
    .get(rt.SPECIE)
    .then(res => createOptionList(res, 'id_e', 'nombre_cientifico'))
    .catch(err => err.response.data)
}

export const getOptionsSpecies = () => {
  return getSpecieAll()
    .then(res => {
      const sorted = Object.keys(res).sort((a, b) =>
        res[a].localeCompare(res[b], 'es', { sensitivity: 'accent' })
      )
      return (
        sorted?.map(key => ({
          title: res[key],
          id: parseInt(key, 10),
        })) ?? []
      )
    })
    .catch(error => error)
}

export const getSpecieTableAll = () => {
  return axiosInstance
    .get(rt.SPECIE)
    .then(res => {
      return _.map(res.data.res, row => ({
        id: row.id_e,
        nombre_cientifico: row.nombre_cientifico,
        clasificador: row.clasificador ?? row.classifier ?? row.clasification ?? '',
        idFamilia: row.idFamilia,
        habilitado: Boolean(row.habilitado.data[0]),
        estado_validacion: row.estado_validacion ?? 'pendiente',
      }))
    })
    .catch(err => err.response.data)
}

export const updateSpecieTableById = (data, token, specieId) => {
  return axiosInstance({
    method: 'put',
    url: `${rt.SPECIE_UPDATE}/${specieId}`,
    data,
  })
    .then(response => response.data)
    .catch(err => err.response.data)
}

export const getSpecieTreeCount = (specieId, specieName = null) => {
  const getCountFromAdvancedFilters = async () => {
    const normalizedName = (specieName || '').trim()
    const filterPayload = [
      {
        type: 'specie',
        filterList1: [{ id: Number(specieId), title: normalizedName || String(specieId) }],
        filterList2: [],
      },
    ]

    const requests = [
      { data: filterPayload },
      { data: filterPayload, canton: 'loja' },
    ]

    for (const payload of requests) {
      try {
        const response = await axiosInstance({
          method: 'post',
          url: rt.GEOJSON_FILTERS_ADVANCED,
          data: payload,
        })
        const features = response?.data?.response?.features
        if (!Array.isArray(features)) continue
        return {
          count: features.length,
          source: 'geojson-filters-advanced',
        }
      } catch (error) {
        if (error?.response?.status !== 404) {
          // sigue con el siguiente intento
        }
      }
    }

    return {
      error: true,
      status: 500,
      message: 'No se pudo interpretar el conteo de árboles asociados.',
    }
  }
  return getCountFromAdvancedFilters().catch(fallbackError => ({
    ...(fallbackError?.response?.data || {}),
    error: true,
    status: fallbackError?.response?.status || 500,
    message:
      fallbackError?.response?.data?.message ||
      'No se pudo verificar árboles asociados a la especie.',
  }))
}

export const disableSpecieById = (specieId, fallbackPayload = null) =>
  axiosInstance
    .put(`${rt.SPECIE_DISABLE}/${specieId}`)
    .then(res => res.data)
    .catch(err => ({ ...(err.response?.data || {}), error: true, status: err.response?.status, message: err.response?.data?.message || 'No se pudo deshabilitar la especie.' }))

export const permanentlyDeleteSpecieById = specieId =>
  axiosInstance
    .delete(`${rt.SPECIE}/delete/${specieId}`)
    .then(res => res.data)
    .catch(err => ({ ...(err.response?.data || {}), error: true, status: err.response?.status, message: err.response?.data?.message || 'No se pudo eliminar la especie.' }))

export const createSpecie = (data, token) => {
  return axiosInstance({
    method: 'post',
    url: rt.SPECIE_CREATE,
    data,
  })
    .then(response => response.data)
    .catch(err => err.response.data)
}

export const getParishAll = (canton = null) => {
  const url = canton ? `${rt.PARISH}/by-canton-slug/${canton}` : rt.PARISH
  return axiosInstance
    .get(url)
    .then(res => {
      // Handle new response format with parroquias array
      const parishes = res.data.parroquias || res.data.res || []
      let data = {}
      parishes.forEach(p => {
        const id = p.id || p.id_pa
        const nombre = p.nombre
        const habilitado = p.habilitado !== undefined ? p.habilitado : p.habilitado?.data?.[0]
        if (habilitado) {
          data[id] = nombre
        }
      })
      return data
    })
    .catch(err => err.response?.data || {})
}

export const getCategoryAll = () => {
  return axiosInstance
    .get(rt.CATEGORY)
    .then(res => Object.values(createOptionList(res, 'id_ctg', 'nombre')))
    .catch(err => err.response.data)
}

export const getCategoryTypeAll = () => {
  return axiosInstance
    .get(rt.CATEGORY_TYPE)
    .then(res => res.data)
    .catch(err => err.response.data)
}

export const getMetaFeaturesAll = () => {
  return axiosInstance
    .get(rt.META_FEATURE)
    .then(res => res.data)
    .catch(err => err.response.data)
}

export const getOptionsMetaFeaturesAll = () => {
  return getMetaFeaturesAll()
    .then(data => {
      const order = [15, 11, 14]
      const items = _.filter(data.res, obj => _.includes(order, obj.id_mc))
      const sorted = _.sortBy(items, obj => _.indexOf(order, obj.id_mc))

      return _.map(sorted, obj => {
        const optionsInside = _.split(obj.u_medida, ',')
        const purgeList = _.map(optionsInside, (v, i) => ({
          id: i + 1,
          title: _.trim(v),
        }))
        return {
          id: obj.key,
          title: obj.nombre,
          options: purgeList,
        }
      })
    })
    .catch(err => console.log('Error :>> ', err))
}

export const getCTByCategory = categoryID => {
  return axiosInstance
    .get(`${rt.CATEGORY_TYPE_BY_ID}/${categoryID}`)
    .then(res =>
      res.data.res.map(obj => ({
        chip: true,
        category: categoryID,
        typecategory: obj.id_ctgT,
        name: obj.key,
        label: obj.descripcion,
        initvalue: '',
      }))
    )
    .catch(err => err.response.data)
}

export const disableInconveniences = (treeId, data, token) => {
  return axiosInstance({
    method: 'put',
    url: `${rt.INCONVENIENCE_DISABLE}/${treeId}`,
    data,
  })
    .then(response => response.data.res)
    .catch(error => serviceErrorData(error))
}

export const createFeaturesByKey = (treeId, data, token) => {
  return axiosInstance({
    method: 'post',
    url: `${rt.FEATURE_UPDATE}/${treeId}`,
    data,
  })
    .then(res => res.data.response)
    .catch(err => err?.response?.data?.response ?? null)
}
export const createAFeature = (treeId, data, token) => {
  return axiosInstance({
    method: 'post',
    url: `${rt.FEATURE_CREATE}/${treeId}`,
    data,
  })
    .then(res => res.data.typeAction)
    .catch(err => err.response.data.typeAction)
}

export const createAndUpdateInconvenienceLog = (treeId, data, token) => {
  return axiosInstance({
    method: 'post',
    url: `${rt.INCONVENIENCE_UPDATE}/${treeId}`,
    data,
  })
    .then(response => response.data.res)
    .catch(error => serviceErrorData(error))
}

export const updateTreeTable = (treeId, data, token) => {
  return axiosInstance({
    method: 'put',
    url: `${rt.TREE_UPDATE}/${treeId}`,
    data,
  })
    .then(response => response.data)
    .catch(error => serviceErrorData(error))
}

export const reassignTree = (treeId, data, token) => {
  return axiosInstance({
    method: 'put',
    url: `${rt.TREE_REASSIGN}/${treeId}`,
    data,
  })
    .then(response => response.data)
    .catch(error => error.response?.data || { success: false })
}

export const registerTreeComplete = (data, token) => {
  return axiosInstance({
    method: 'post',
    url: rt.TREE_REGISTER_COMPLETE,
    data,
  })
    .then(response => response.data)
    .catch(error => serviceErrorData(error))
}

export const getGoogleCodePreviewByCoords = async ({ lat, lng }) => {
  const safeLat = Number(lat)
  const safeLng = Number(lng)
  if (Number.isNaN(safeLat) || Number.isNaN(safeLng)) return null

  const normalizePlusCode = raw => {
    const cleaned = String(raw || '')
      .replace(/^gps[\s:.-]*/i, '')
      .replace(/^plus\s*code[\s:.-]*/i, '')
      .trim()
    if (!cleaned) return null

    // Keep only the Plus Code segment (without possible tree suffix like A/B/C...)
    const plusCodeMatch = cleaned.match(/([23456789CFGHJMPQRVWX]{4,8}\+[23456789CFGHJMPQRVWX]{2,})/i)
    return (plusCodeMatch?.[1] || cleaned).toUpperCase()
  }

  const requestPayload = {
    lat: safeLat,
    lng: safeLng,
    latitude: safeLat,
    longitude: safeLng,
    long: safeLng,
    latY: safeLat,
    longX: safeLng,
  }

  const endpointCandidates = [
    rt.TREE_PREVIEW_GOOGLE_CODE,
    rt.TREE_PREVIEW_PLUS_CODE,
    rt.TREE_GOOGLE_CODE_PREVIEW,
  ]

  for (const endpoint of endpointCandidates) {
    try {
      const response = await axiosInstance.post(endpoint, requestPayload)
      const data = response?.data || {}

      const rawCode = [
        data.googleCode,
        data.google_code,
        data.googleCodePreview,
        data.google_code_preview,
        data.plusCode,
        data.plus_code,
        data.previewCode,
        data.preview_code,
        data.preview?.googleCode,
        data.preview?.google_code,
        data.preview?.plusCode,
        data.response?.googleCode,
        data.response?.google_code,
        data.response?.googleCodePreview,
        data.response?.google_code_preview,
        data.response?.plusCode,
        data.data?.googleCode,
        data.data?.google_code,
        data.data?.googleCodePreview,
        data.data?.google_code_preview,
        data.result?.googleCode,
        data.result?.google_code,
        data.result?.googleCodePreview,
        data.result?.google_code_preview,
        data.code,
        data.codigo,
      ].find(v => typeof v === 'string' && v.trim())

      if (!rawCode) continue

      const normalized = normalizePlusCode(rawCode)
      if (normalized) return normalized
    } catch (error) {
      // Try next compatible backend route for preview.
      continue
    }
  }

  return null
}

export const getZoneTypesAll = () => {
  return axiosInstance
    .get(`${rt.ZONE_TYPES}?habilitado=true&includeCompatibility=true`)
    .then(response => {
      const zoneTypes = response.data || []
      const options = {}
      let defaultId = null
      const fullData = {}
      zoneTypes.forEach(zt => {
        options[zt.id] = zt.nombre
        if (zt.porDefecto) defaultId = String(zt.id)
        fullData[zt.id] = zt
      })
      return { options, defaultId, fullData }
    })
    .catch(err => {
      console.error('Error fetching zone types:', err)
      return { options: {}, defaultId: null, fullData: {} }
    })
}

export const getSiteTypesAll = () => {
  return axiosInstance
    .get(`${rt.SITE_TYPES}?habilitado=true`)
    .then(response => {
      const siteTypes = response.data || []
      const options = {}
      let defaultId = null
      const fullData = {}
      siteTypes.forEach(st => {
        options[st.id] = st.nombre
        if (st.porDefecto) defaultId = String(st.id)
        fullData[st.id] = st
      })
      return { options, defaultId, fullData }
    })
    .catch(err => {
      console.error('Error fetching site types:', err)
      return { options: {}, defaultId: null, fullData: {} }
    })
}

export const getZoneTypesAsFilterOptions = () => {
  return axiosInstance
    .get(`${rt.ZONE_TYPES}?habilitado=true`)
    .then(response => {
      const zoneTypes = response.data || []
      return zoneTypes.map(zt => ({ id: zt.id, title: zt.nombre }))
    })
    .catch(err => {
      console.error('Error fetching zone types:', err)
      return []
    })
}

export const getSiteTypesAsFilterOptions = () => {
  return axiosInstance
    .get(`${rt.SITE_TYPES}?habilitado=true`)
    .then(response => {
      const siteTypes = response.data || []
      return siteTypes.map(st => ({ id: st.id, title: st.nombre }))
    })
    .catch(err => {
      console.error('Error fetching site types:', err)
      return []
    })
}

export const getSpeciesByFamilyId = familyId => {
  return axiosInstance
    .get(`${rt.SPECIE_FOR_FAMILY}/${familyId}`)
    .then(response => response.data.res)
    .catch(error => error.response.data)
}

export const specieIncludedInFamily = (familyId, specieId) => {
  return axiosInstance({
    method: 'get',
    url: rt.SPECIE_VERIFIED_WITH_FAMILY,
    params: {
      familyId,
      specieId,
    },
  })
    .then(response => response.data.res)
    .catch(error => error.response.data)
}

export const getSiteNameAll = () => {
  return axiosInstance
    .get(rt.SITE)
    .then(response => {
      const dataUnique = _.uniqBy(response.data.res, 'nombre')
      const listSiteName = _.map(dataUnique, 'nombre')
      return listSiteName
    })
    .catch(error => error.response.data)
}

export const getSiteAll = () => {
  return axiosInstance
    .get(rt.SITE)
    .then(response => response.data?.res)
    .catch(error => error.response.data)
}

export const getSiteOptionsByTenant = tenantId => {
  return axiosInstance
    .get(rt.SITE, { params: { tenantId } })
    .then(response => {
      const sites = response.data?.res || []
      return sites
        .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'accent' }))
        .map(s => ({ id: s.id_s, title: s.nombre }))
    })
    .catch(() => [])
}

export const getSitesByParish = parishId => {
  return axiosInstance
    .get(`${rt.SITE_BY_PARISH}/${parishId}`)
    .then(response => response.data?.sites || [])
    .catch(error => {
      console.error('[getSitesByParish] Error:', error)
      return []
    })
}

export const getOptionsSite = () => {
  return getSiteAll()
    .then(response => {
      const uniq = _.unionBy(response, 'nombre')
      const sorted = uniq.sort((a, b) =>
        a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'accent' })
      )
      return (
        sorted?.map(o => ({
          title: o.nombre,
          id: o.id_s,
        })) ?? []
      )
    })
    .catch(error => error)
}

export const getSiteFilterOptions = async (canton = null, tenant = null) => {
  const params = new URLSearchParams()
  if (canton) params.append('canton', canton)
  if (tenant) params.append('tenant', tenant)
  const query = params.toString()
  const url = `${rt.SITE}/filter-options${query ? `?${query}` : ''}`
  const response = await axiosInstance.get(url)
  return response.data.sites || []
}

export const getPseudomynAll = () => {
  return axiosInstance
    .get(rt.PSEUDONYM)
    .then(response => response.data?.pseudonym)
    .catch(error => error.response.data)
}

export const getOptionsPseudonym = () => {
  return getPseudomynAll()
    .then(response => {
      const uniq = _.unionBy(response, 'nombre_comun')
      //localeCompare para tildes
      const sorted = uniq.sort((a, b) =>
        a.nombre_comun.localeCompare(b.nombre_comun, 'es', { sensitivity: 'accent' })
      )
      return (
        sorted?.map(o => ({
          title: o.nombre_comun,
          id: o.id_ps,
        })) ?? []
      )
    })
    .catch(error => error)
}

export const getSiteSectionAll = () => {
  return axiosInstance
    .get(rt.SITE)
    .then(response => {
      const sites = response.data.res || []
      const sections = []
      sites.forEach(site => {
        (site.tenants || []).forEach(t => {
          (t.secciones || []).forEach(s => {
            if (s.codigo) sections.push(s.codigo)
          })
        })
      })
      return [...new Set(sections)].sort()
    })
    .catch(error => error.response?.data)
}

export const createSiteTable = (
  { siteName, typeSite, sitePlace, section, parishId, siteAcronym },
  token
) => {
  return axiosInstance({
    method: 'post',
    url: rt.SITE_CREATE,
    data: {
      sitio: siteName,
      seccion: section,
      idTipoZona: typeSite !== '#' ? typeSite : null,
      idTipoSitio: sitePlace !== '#' ? sitePlace : null,
      idParroquia: parishId !== '#' ? parishId : null,
      acronimoSitio: siteAcronym,
    },
  })
    .then(response => response.data.action)
    .catch(error => serviceErrorData(error))
}

export const createPseudonyms = (data, token) => {
  return axiosInstance({
    method: 'post',
    url: rt.PSEUDONYM_BACTH,
    data: {
      data,
    },
  })
    .then(response => {
      return response.data.actions
    })
    .catch(error => error.response.data)
}

export const deletePseudonymsOfSpecie = (data, token) => {
  return axiosInstance({
    method: 'put',
    url: rt.PSEUDONYM_DELETE,
    data: {
      data,
    },
  })
    .then(response => response.data.actions)
    .catch(error => error.response.data)
}

export const activePseudonymsOfSpecie = (data, token) => {
  return axiosInstance({
    method: 'put',
    url: rt.PSEUDONYM_ACTIVE,
    data: {
      data,
    },
  })
    .then(response => response.data.actions)
    .catch(error => error.response.data)
}

export const getPseudonymTableAll = () => {
  return axiosInstance({
    method: 'get',
    url: rt.PSEUDONYM_ADMIN_LIST,
  })
    .then(res => {
      return res.data.pseudonyms.map(p => ({
        id: p.id,
        nombre_comun: p.nombre_comun,
        idEspecie: p.idEspecie,
        nombre_cientifico: p.nombre_cientifico,
        habilitado: typeof p.habilitado === 'object'
          ? Boolean(p.habilitado?.data?.[0])
          : Boolean(p.habilitado),
        estado_validacion: p.estado_validacion ?? 'pendiente',
      }))
    })
    .catch(err => err.response?.data)
}

export const updatePseudonymById = (data, token, pseudonymId) => {
  return axiosInstance({
    method: 'put',
    url: `${rt.PSEUDONYM_UPDATE}/${pseudonymId}`,
    data,
  })
    .then(response => response.data)
    .catch(err => err.response?.data)
}

export const getPseudonymTreeCount = (pseudonymId, pseudonymName = null) => {
  const normalizedName = (pseudonymName || '').trim()
  const filterPayload = [
    {
      type: 'pseudonym',
      filterList1: [{ id: Number(pseudonymId), title: normalizedName || String(pseudonymId) }],
      filterList2: [],
    },
  ]

  const requests = [
    { data: filterPayload },
    { data: filterPayload, canton: 'loja' },
  ]

  const tryWithPayload = async index => {
    if (index >= requests.length) {
      return {
        error: true,
        status: 500,
        message: 'No se pudo verificar árboles asociados al pseudónimo.',
      }
    }

    try {
      const response = await axiosInstance({
        method: 'post',
        url: rt.GEOJSON_FILTERS_ADVANCED,
        data: requests[index],
      })
      const features = response?.data?.response?.features
      if (!Array.isArray(features)) return tryWithPayload(index + 1)
      return {
        count: features.length,
        source: 'geojson-filters-advanced',
      }
    } catch {
      return tryWithPayload(index + 1)
    }
  }

  return tryWithPayload(0).catch(error => ({
    ...(error?.response?.data || {}),
    error: true,
    status: error?.response?.status || 500,
    message:
      error?.response?.data?.message ||
      'No se pudo verificar árboles asociados al pseudónimo.',
  }))
}

export const disablePseudonymById = (pseudonymId, fallbackPayload = null) => {
  const requests = [
    { method: 'put', url: `${rt.PSEUDONYM}/disable/${pseudonymId}` },
    { method: 'patch', url: `${rt.PSEUDONYM}/disable/${pseudonymId}` },
  ]

  const fallbackDisable = async () => {
    if (!fallbackPayload) {
      return { error: true, status: 404, message: 'No se pudo deshabilitar el pseudónimo.' }
    }
    return updatePseudonymById({ ...fallbackPayload, habilitado: false }, null, pseudonymId)
  }

  const attempt = async index => {
    if (index >= requests.length) return fallbackDisable()
    try {
      const response = await axiosInstance(requests[index])
      return response.data
    } catch (error) {
      if (error?.response?.status === 404) return attempt(index + 1)
      return fallbackDisable()
    }
  }

  return attempt(0)
}

export const permanentlyDeletePseudonymById = pseudonymId => {
  const requests = [
    { method: 'delete', url: `${rt.PSEUDONYM}/${pseudonymId}` },
    { method: 'delete', url: `${rt.PSEUDONYM}/delete/${pseudonymId}` },
    { method: 'delete', url: `${rt.PSEUDONYM}/delete-pseudonym/${pseudonymId}` },
  ]

  const attempt = async index => {
    if (index >= requests.length) {
      return { error: true, status: 404, message: 'No se pudo eliminar el pseudónimo.' }
    }
    try {
      const response = await axiosInstance(requests[index])
      return response.data
    } catch (error) {
      if (error?.response?.status === 404) return attempt(index + 1)
      return {
        ...(error?.response?.data || {}),
        error: true,
        status: error?.response?.status,
        message: error?.response?.data?.message || 'No se pudo eliminar el pseudónimo.',
      }
    }
  }

  return attempt(0)
}

export const createPseudonymSingle = (data, token) => {
  return axiosInstance({
    method: 'post',
    url: `${rt.PSEUDONYM}/register-pseudonym`,
    data,
  })
    .then(response => response.data)
    .catch(error => error.response?.data)
}

export const getSiteAcronymAll = () => {
  return axiosInstance
    .get(rt.SITE)
    .then(response => {
      const sites = response.data.res || []
      const acronyms = []
      sites.forEach(site => {
        (site.tenants || []).forEach(t => {
          if (t.acronimoSitio) acronyms.push(t.acronimoSitio)
        })
      })
      return [...new Set(acronyms)].sort()
    })
    .catch(error => error.response?.data)
}

export const activateTenant = (tenantId, token) => {
  return axiosInstance
    .post(rt.AUTH_ACTIVATE_TENANT, { tenantId })
    .then(response => response.data)
    .catch(error => error.response?.data)
}

export const isUserVerify = token => {
  return axiosInstance
    .get(rt.AUTH_VERIFY, { headers: { authorization: token } })
    .then(response => response.data)
    .catch(error => {
      if (isMaintenanceResponse(error)) return { code: 'MAINTENANCE', maintenance: true }
      return error.response?.data
    })
}

export const getUserById = (id, token) => {
  return axiosInstance({
    method: 'get',
    url: `${rt.APP_USER}/${id}`,
    headers: { authorization: token },
  })
    .then(response => response.data)
    .catch(error => error.response?.data)
}

export const updateStatusTreeRegister = (treeId, data, token) => {
  return axiosInstance({
    method: 'put',
    url: `${rt.TREE_UPDATE_STATUS_REGISTER}/${treeId}`,
    data,
  })
    .then(response => response.data)
    .catch(error => error.response.data)
}

export const deleteTree = (treeId, data, token) => {
  return axiosInstance({
    method: 'put',
    url: `${rt.TREE_DELETE}/${treeId}`,
    data,
  })
    .then(response => response.data)
    .catch(error => error.response.data)
}

export const isValidTree = (treeId, canton = null) => {
  const url = canton
    ? `${rt.TREE_VALID}/${treeId}?canton=${canton}`
    : `${rt.TREE_VALID}/${treeId}`
  return axiosInstance({
    method: 'get',
    url,
  })
    .then(response => response.data)
    .catch(error => {
      // Diferenciar entre error de servidor y respuesta de validación
      const status = error.response?.status
      const data = error.response?.data

      // Si el servidor devolvió datos de validación (ej: 404 con valid: false)
      if (data && typeof data.valid === 'boolean') {
        return data
      }

      // Error 500 u otros errores del servidor - propagar el error
      if (status >= 500) {
        console.error('[isValidTree] Error del servidor:', status, data)
        return { valid: false, serverError: true, message: data?.message || 'Error del servidor' }
      }

      // Otros errores (red, timeout, etc.)
      return { valid: false, serverError: true, message: error.message || 'Error de conexión' }
    })
}

export const resolveTreeRoute = identifier => {
  if (!identifier) return Promise.resolve({ found: false, message: 'Identificador requerido' })

  return axiosInstance
    .get(`${rt.TREE_RESOLVE}/${encodeURIComponent(identifier)}`)
    .then(response => response.data)
    .catch(error => {
      const data = error.response?.data
      if (data && typeof data.found === 'boolean') return data
      return {
        found: false,
        serverError: true,
        message: data?.message || error.message || 'Error al resolver la ruta del árbol',
      }
    })
}

export const getReportHeader = token => {
  return axiosInstance
    .get(rt.REPORT_HEADERS)
    .then(response => response.data)
    .catch(error => error.response.data)
}

export const getReportData = async ({
  tenantId, page = 1, limit = 20, habilitado = 'true',
  especieId, parroquiaId, tipoZonaId, sitioId, signal,
} = {}) => {
  const params = { page, limit, habilitado }
  if (tenantId) params.tenantId = tenantId
  if (especieId) params.especieId = especieId
  if (parroquiaId) params.parroquiaId = parroquiaId
  if (tipoZonaId) params.tipoZonaId = tipoZonaId
  if (sitioId) params.sitioId = sitioId
  const response = await axiosInstance.get(rt.REPORT_DATA, { params, signal })
  return response.data
}

// Límite máximo permitido por el backend (ALLOWED_LIMITS = [20, 50, 100]).
const REPORT_MAX_PAGE_LIMIT = 100
// Páginas a solicitar en paralelo por lote, para no saturar el backend.
const REPORT_FETCH_CONCURRENCY = 5

// Trae el 100% de las filas del reporte (todas las páginas) respetando los filtros.
// Reutiliza getReportData paginando con el límite máximo permitido.
export const getAllReportData = async ({
  tenantId, habilitado = 'true', especieId, parroquiaId, tipoZonaId, sitioId, signal,
} = {}) => {
  const baseFilters = { tenantId, habilitado, especieId, parroquiaId, tipoZonaId, sitioId, signal }

  const firstPage = await getReportData({ ...baseFilters, page: 1, limit: REPORT_MAX_PAGE_LIMIT })
  const total = firstPage?.pagination?.total ?? 0
  const totalPages = firstPage?.pagination?.pages ?? 0

  let rows = firstPage?.resultALL ?? []

  for (let page = 2; page <= totalPages; page += REPORT_FETCH_CONCURRENCY) {
    const batch = []
    for (let offset = 0; offset < REPORT_FETCH_CONCURRENCY && page + offset <= totalPages; offset += 1) {
      batch.push(getReportData({ ...baseFilters, page: page + offset, limit: REPORT_MAX_PAGE_LIMIT }))
    }
    const responses = await Promise.all(batch)
    rows = responses.reduce((accum, response) => accum.concat(response?.resultALL ?? []), rows)
  }

  return { resultALL: rows, total }
}

export const verifyEmail = email => {
  return axiosInstance({
    method: 'post',
    url: rt.AUTH_VERIFY_USER,
    data: { email },
  })
    .then(response => response.data)
    .catch(error => error.response.data)
}

export const getUserMe = token => {
  return axiosInstance({
    method: 'get',
    url: rt.APP_USER_ME,
  })
    .then(response => response.data)
    .catch(error => error.response.data)
}

export const updateUser = (data, token) => {
  return axiosInstance({
    method: 'put',
    url: rt.APP_USER_UPDATE_ACCOUNT,
    data,
  })
    .then(response => response.data)
    .catch(error => error.response.data)
}

export const updateUserById = (data, token, userId) => {
  return axiosInstance({
    method: 'put',
    url: `${rt.APP_USER}/${userId}`,
    data,
  })
    .then(response => response.data)
    .catch(error => error.response.data)
}

export const getUserTreeCount = userId => {
  return axiosInstance({
    method: 'get',
    url: `${rt.APP_USER}/${userId}/trees`,
  })
    .then(response => response.data)
    .catch(error => error.response?.data || { count: 0 })
}

export const disableUserById = userId => {
  return axiosInstance({
    method: 'put',
    url: `${rt.APP_USER}/disable/${userId}`,
  })
    .then(response => response.data)
    .catch(error => error.response?.data)
}

export const permanentlyDeleteUserById = userId => {
  return axiosInstance({
    method: 'delete',
    url: `${rt.APP_USER}/${userId}`,
  })
    .then(response => response.data)
    .catch(error => error.response?.data)
}

export const adminResetUserPassword = userId => {
  return axiosInstance({
    method: 'post',
    url: `${rt.APP_USER}/${userId}/reset-password`,
  })
    .then(response => response.data)
    .catch(error => error.response?.data)
}

export const getUserAll = token => {
  return axiosInstance({
    method: 'get',
    url: rt.APP_USER,
  })
    .then(response => {
      let res = []
      if (response.data?.auth) {
        res = _.map(response.data.users, user => ({
          id: user.id_u,
          nombre: user.nombre,
          apellido: user.apellido,
          email: user.email,
          habilitado: Boolean(user.habilitado.data[0]),
          fecha_registro: moment(user.fecha_registro).utc().format('LL'),
          fecha_registro_raw: user.fecha_registro ? new Date(user.fecha_registro) : null,
          fecha_actualizo: moment(user.fecha_actualizo).utc().format('LL'),
          idRol: user.idRol,
          esPublico: Boolean(user.esPublico),
        }))
      }
      return res
    })
    .catch(error => console.log('Error', error.response.data))
}

export const createUser = data => {
  return axiosInstance({
    method: 'post',
    url: rt.APP_USER_REGISTER_USER,
    data,
  })
    .then(response => response.data)
    .catch(error => error.response.data)
}

// Alta desde la gestión de usuarios: la cuenta nace aprobada y el backend envía el código temporal.
export const createUserByAdmin = data => {
  return axiosInstance({
    method: 'post',
    url: rt.APP_USER,
    data,
  })
    .then(response => response.data)
    .catch(
      error =>
        error.response?.data ?? {
          created: false,
          message: 'No se pudo conectar con el servidor. Inténtelo nuevamente.',
        },
    )
}

export const sendEmailResetPassword = email => {
  return axiosInstance({
    method: 'post',
    url: rt.AUTH_SEND_EMAIL_RESET_PASSWORD,
    data: { email },
  })
    .then(response => response.data)
    .catch(error => error.response.data)
}

export const approveUserAccount = (data, token) => {
  return axiosInstance({
    method: 'put',
    url: rt.AUTH_APPROVE_USER_ACCOUNT,
    data,
    headers: { authorization: token },
  })
    .then(response => response.data)
    .catch(error => error.response?.data)
}

export const resetPassword = (data, token) => {
  return axiosInstance({
    method: 'put',
    url: rt.APP_USER_UPDATE_PASSWORD,
    data,
    headers: { Authorization: token },
  })
    .then(response => response.data)
    .catch(error => error.response?.data)
}

export const getTypeRequested = () => {
  return axiosInstance({
    method: 'get',
    url: rt.TYPE_REQUESTED,
  })
    .then(response => response.data)
    .catch(error => error.response.data)
}

export const createMeasuringGroup = (data, token) => {
  return axiosInstance({
    method: 'post',
    url: rt.TYPE_REQUESTED,
    data,
  })
    .then(response => response.data)
    .catch(error => error.response.data)
}

export const getRole = token => {
  return axiosInstance({
    method: 'get',
    url: rt.ROLE,
  })
    .then(response => response.data)
    .catch(error => error.response.data)
}

export const getReportHeaderPublic = () => {
  return axiosInstance
    .get(rt.REPORT_PUBLIC_HEADERS)
    .then(response => response.data)
    .catch(error => error.response.data)
}

export const getReportDataPublic = async () => {
  try {
    const response = await axiosInstance.get(rt.REPORT_PUBLIC_DATA)
    return response.data
  } catch (error) {
    throw new Error(error.error)
  }
}

export const getTreeRegisterCount = () => {
  return axiosInstance
    .get(rt.REPORT_COUNT_TREE)
    .then(response => response.data.count)
    .catch(error => error.response.data)
}

export const getSpecieRegisterCount = () => {
  return axiosInstance
    .get(rt.SPECIE_TOTAL)
    .then(response => response.data.count)
    .catch(error => error.response.data)
}

export const getFamilyRegisterCount = () => {
  return axiosInstance
    .get(rt.FAMILY_TOTAL)
    .then(response => response.data.count)
    .catch(error => error.response.data)
}

export const getGenderRegisterCount = () => {
  return axiosInstance
    .get(rt.SPECIE_GENDER_TOTAL)
    .then(response => response.data.count)
    .catch(error => error.response.data)
}

export const getAdvancedSearch = (filtersList, canton = null, tenant = null, options = {}) => {
  const enabledOnly = getEnabledOnlyOption(options)
  const data = filtersList.map(item => ({
    type: item.type,
    filterList1: item.filterList1.map(f => ({ id: f.id, title: f.title })),
    filterList2: item.filterList2.map(f => ({ id: f.id, title: f.title })),
  }))

  return axiosInstance({
    method: 'post',
    url: rt.GEOJSON_FILTERS_ADVANCED,
    data: {
      data,
      ...(canton && { canton }),
      ...(tenant && { tenant }),
      ...(enabledOnly && { habilitado: true }),
    },
  })
    .then(res =>
      filterEnabledGeojson(
        res.data.response || { type: 'FeatureCollection', features: [] },
        enabledOnly,
      )
    )
    .catch(error => {
      console.log('Error :>> ', error.response.data)
      return {}
    })
}

export const getOptionsCodes = () => {
  return axiosInstance
    .get(rt.TREE_ENABLE_CODES)
    .then(response => {
      const sorted = response?.data?.codes?.sort((a, b) => {
        return a.codigo.localeCompare(b.codigo, 'es', {
          numeric: true,
          sensitivity: 'base',
        })
      })
      return sorted?.map((o, i) => ({ id: i, title: o.codigo })) ?? []
    })
    .catch(error => error)
}

export const getOptionsCategoryAll = categoryID => {
  return axiosInstance({
    method: 'get',
    url: `${rt.CATEGORY_TYPE_BY_ID}/${categoryID}`,
  })
    .then(response => {
      const data = response?.data?.res
      _.remove(data, obj => _.includes([32, 86, 87, 88, 89, 90, 91, 92], obj.id_ctgT))
      const uniqKeyList = _.uniq(_.map(data, 'key'))
      const result = _.map(uniqKeyList, key => {
        const filtered = _.filter(data, ['key', key])
        const options = _.map(filtered, (o, i) => ({ id: i + 1, title: o.enum }))

        return {
          id: filtered[0].key,
          title: filtered[0].descripcion,
          options,
        }
      })
      return _.sortBy(result, item => _.toLower(item.title))
    })
    .catch(error => {
      console.log('Error :>> ', error.response.data)
      return {}
    })
}

export const updateDownloadCounter = () => {
  return axiosInstance({
    method: 'post',
    url: rt.PROJECT_UPDATE_DOWNLOAD_COUNTER,
  })
    .then(res => res?.data?.update || false)
    .catch(error => {
      console.log('Error :>> ', error.response.data)
      return false
    })
}

export const getDownloadCounter = () => {
  return axiosInstance({
    method: 'get',
    url: rt.PROJECT,
  })
    .then(res => res?.data?.res?.[0]?.descargaDB ?? 0)
    .catch(error => {
      console.log('Error :>> ', error?.response?.data)
      return 0
    })
}

export const postContactForm = async data => {
  try {
    const response = await axiosInstance({
      method: 'post',
      url: rt.CONTACT_FORM,
      data,
    })
    if (response.data?.send === 'ok') {
      return { success: true, message: response.data.message }
    }
    return { success: false, message: response.data?.message }
  } catch (error) {
    return {
      success: false,
      message: error?.response?.data?.message,
    }
  }
}

export const getProjectsAll = () => {
  return axiosInstance
    .get(rt.PROJECT)
    .then(res => res?.data?.res || [])
    .catch(err => err.response?.data || [])
}

export const getNewsAll = () => {
  return axiosInstance
    .get(rt.NEWS_API)
    .then(res => res?.data?.res || [])
    .catch(err => err.response?.data || [])
}

export const getNewsEnabled = () => {
  return axiosInstance
    .get(rt.NEWS_API)
    .then(res => {
      const items = res?.data?.res || []
      return _.filter(items, n => {
        if (typeof n?.habilitado === 'boolean') return n.habilitado
        if (n?.habilitado !== undefined) return Boolean(n.habilitado?.data?.[0])
        return true
      })
    })
    .catch(err => err.response?.data || [])
}

export const getNewsTableAll = () => {
  return axiosInstance
    .get(rt.NEWS_API)
    .then(res => {
      const items = res?.data?.res || []
      return _.map(items, row => ({
        id: row.id_noticia,
        titulo: row.titulo,
        url: row.url,
        contenido: row.contenido,
        fechaRegistro: row.fechaRegistro,
        habilitado:
          typeof row.habilitado === 'boolean'
            ? row.habilitado
            : row.habilitado !== undefined
            ? Boolean(row.habilitado?.data?.[0])
            : true,
        idProyecto: row.idProyecto,
        foto: row.foto,
      }))
    })
    .catch(err => err.response?.data || [])
}

export const createNews = (data, token) => {
  return axiosInstance({
    method: 'post',
    url: `${rt.NEWS_API}/register-new`,
    data,
  })
    .then(response => response.data)
    .catch(err => err.response?.data || err)
}

export const updateNewsById = (data, newsId, token) => {
  return axiosInstance({
    method: 'put',
    url: `${rt.NEWS_API}/update-new/${newsId}`,
    data,
  })
    .then(response => response.data)
    .catch(err => err.response?.data || err)
}

export const deleteNewsById = (newsId, token) => {
  return axiosInstance({
    method: 'delete',
    url: `${rt.NEWS_API}/delete-new/${newsId}`,
  })
    .then(response => response.data)
    .catch(err => err.response?.data || err)
}

// Public Events
export const getPublicEventsAll = () => {
  return axiosInstance
    .get(rt.PUBLIC_EVENTS_API)
    .then(res => res?.data?.events || [])
    .catch(err => err.response?.data || [])
}

export const createPublicEvent = (data) => {
  return axiosInstance({
    method: 'post',
    url: `${rt.PUBLIC_EVENTS_API}/create`,
    data,
  })
    .then(response => response.data)
    .catch(err => err.response?.data || err)
}

export const updatePublicEvent = (data, eventId) => {
  return axiosInstance({
    method: 'put',
    url: `${rt.PUBLIC_EVENTS_API}/${eventId}`,
    data,
  })
    .then(response => response.data)
    .catch(err => err.response?.data || err)
}

export const deactivatePublicEvent = (eventId) => {
  return axiosInstance({
    method: 'delete',
    url: `${rt.PUBLIC_EVENTS_API}/${eventId}`,
  })
    .then(response => response.data)
    .catch(err => err.response?.data || err)
}

export const getPublicEventFeedback = (eventId) => {
  return axiosInstance
    .get(`${rt.PUBLIC_EVENTS_API}/${eventId}/feedback`)
    .then(res => ({
      feedback: res?.data?.feedback,
      averageRating: res?.data?.averageRating,
    }))
    .catch(err => {
      throw err?.response?.data || err
    })
}

export const getEventsPendingReview = () => {
  return axiosInstance
    .get(`${rt.PUBLIC_EVENTS_API}/my-pending`)
    .then(res => res?.data?.events || [])
    .catch(() => [])
}

export const extendPublicEventDate = (eventId, newEndDate) => {
  return axiosInstance({
    method: 'put',
    url: `${rt.PUBLIC_EVENTS_API}/${eventId}/extend`,
    data: { newEndDate },
  })
    .then(response => response.data)
    .catch(err => err.response?.data || err)
}

export const deletePublicEventTrees = (eventId) => {
  return axiosInstance({
    method: 'delete',
    url: `${rt.PUBLIC_EVENTS_API}/${eventId}/trees`,
  })
    .then(response => response.data)
    .catch(err => err.response?.data || err)
}

export const getPublicEventTrees = (eventId) => {
  return axiosInstance
    .get(`${rt.PUBLIC_EVENTS_API}/${eventId}/trees`)
    .then(res => res?.data?.trees || [])
    .catch(err => {
      throw err?.response?.data || err
    })
}

export const requestDeletePublicEvent = (eventId) =>
  axiosInstance.post(`${rt.PUBLIC_EVENTS_API}/${eventId}/request-delete`)
    .then(r => r.data)
    .catch(err => ({ ...(err.response?.data || {}), error: true, message: err.response?.data?.message || 'Error al solicitar eliminación.' }))

export const confirmDeletePublicEvent = (eventId, code) =>
  axiosInstance({ method: 'delete', url: `${rt.PUBLIC_EVENTS_API}/${eventId}/hard`, data: { code } })
    .then(r => r.data)
    .catch(err => ({ ...(err.response?.data || {}), error: true, message: err.response?.data?.message || 'Error al confirmar eliminación.' }))

// Notifications
export const getMyNotifications = () => {
  return axiosInstance
    .get('/notifications/my')
    .then(res => res?.data || { notifications: [], unreadCount: 0 })
    .catch(() => ({ notifications: [], unreadCount: 0 }))
}

export const markNotificationAsRead = (notificationId) => {
  return axiosInstance
    .put(`/notifications/${notificationId}/read`)
    .then(res => res?.data)
    .catch(err => err.response?.data || err)
}

export const markAllNotificationsRead = () => {
  return axiosInstance
    .put('/notifications/read-all')
    .then(res => res?.data)
    .catch(err => err.response?.data || err)
}

// Tree Photos
export const uploadTreePhotos = (treeId, photos, token) => {
  const formData = new FormData()
  photos.forEach((photo, index) => {
    formData.append('photos', photo.file, photo.originalName || `photo_${index}.webp`)
  })
  return axiosInstance({
    method: 'post',
    url: `${rt.TREE_PHOTOS}/upload/${treeId}`,
    data: formData,
  })
    .then(response => response.data)
    .catch(err => err.response?.data || err)
}

export const getTreePhotos = treeId => {
  return axiosInstance
    .get(`${rt.TREE_PHOTOS}/${treeId}`)
    .then(response => {
      const data = response.data
      // Backend devuelve { photos: [...] }; normalizar a array
      return Array.isArray(data) ? data : Array.isArray(data?.photos) ? data.photos : []
    })
    .catch(() => [])
}

export const deleteTreePhoto = (photoId, token) => {
  return axiosInstance({
    method: 'delete',
    url: `${rt.TREE_PHOTOS}/${photoId}`,
  })
    .then(response => response.data)
    .catch(err => err.response?.data || err)
}

// OTP hard-delete — trees
export const getTreeAdminList = params =>
  axiosInstance.get(rt.TREE_ADMIN_LIST, { params })
    .then(r => r.data)
    .catch(err => ({ ...(err.response?.data || {}), error: true, message: err.response?.data?.message || 'Error al obtener lista de árboles.' }))

export const requestTreeHardDelete = treeId =>
  axiosInstance.post(`${rt.TREE_REQUEST_DELETE}/${treeId}`)
    .then(r => r.data)
    .catch(err => ({ ...(err.response?.data || {}), error: true, message: err.response?.data?.message || 'Error al solicitar eliminación.' }))

export const confirmTreeHardDelete = (treeId, code) =>
  axiosInstance({ method: 'delete', url: `${rt.TREE_HARD_DELETE}/${treeId}`, data: { code } })
    .then(r => r.data)
    .catch(err => ({ ...(err.response?.data || {}), error: true, message: err.response?.data?.message || 'Error al confirmar eliminación.' }))

// OTP hard-delete — families
export const requestFamilyHardDelete = familyId =>
  axiosInstance.post(`${rt.FAMILY_REQUEST_DELETE}/${familyId}`)
    .then(r => r.data)
    .catch(err => ({ ...(err.response?.data || {}), error: true, message: err.response?.data?.message || 'Error al solicitar eliminación.' }))

export const confirmFamilyHardDelete = (familyId, code) =>
  axiosInstance({ method: 'delete', url: `${rt.FAMILY_HARD_DELETE}/${familyId}`, data: { code } })
    .then(r => r.data)
    .catch(err => ({ ...(err.response?.data || {}), error: true, message: err.response?.data?.message || 'Error al confirmar eliminación.' }))

// OTP hard-delete — species
export const requestSpecieHardDelete = specieId =>
  axiosInstance.post(`${rt.SPECIE_REQUEST_DELETE}/${specieId}`)
    .then(r => r.data)
    .catch(err => ({ ...(err.response?.data || {}), error: true, message: err.response?.data?.message || 'Error al solicitar eliminación.' }))

export const confirmSpecieHardDelete = (specieId, code) =>
  axiosInstance({ method: 'delete', url: `${rt.SPECIE_HARD_DELETE}/${specieId}`, data: { code } })
    .then(r => r.data)
    .catch(err => ({ ...(err.response?.data || {}), error: true, message: err.response?.data?.message || 'Error al confirmar eliminación.' }))

// OTP hard-delete — pseudonyms
export const requestPseudonymHardDelete = pseudonymId =>
  axiosInstance.post(`${rt.PSEUDONYM_REQUEST_DELETE}/${pseudonymId}`)
    .then(r => r.data)
    .catch(err => ({ ...(err.response?.data || {}), error: true, message: err.response?.data?.message || 'Error al solicitar eliminación.' }))

export const confirmPseudonymHardDelete = (pseudonymId, code) =>
  axiosInstance({ method: 'delete', url: `${rt.PSEUDONYM_HARD_DELETE}/${pseudonymId}`, data: { code } })
    .then(r => r.data)
    .catch(err => ({ ...(err.response?.data || {}), error: true, message: err.response?.data?.message || 'Error al confirmar eliminación.' }))

// OTP hard-delete — public event trees
export const requestDeletePublicEventTrees = eventId =>
  axiosInstance.post(`${rt.PUBLIC_EVENTS_API}/${eventId}/trees/request-delete`)
    .then(r => r.data)
    .catch(err => ({ ...(err.response?.data || {}), error: true, message: err.response?.data?.message || 'Error al solicitar eliminación.' }))

export const confirmDeletePublicEventTrees = (eventId, code) =>
  axiosInstance({ method: 'delete', url: `${rt.PUBLIC_EVENTS_API}/${eventId}/trees`, data: { code } })
    .then(r => r.data)
    .catch(err => ({ ...(err.response?.data || {}), error: true, message: err.response?.data?.message || 'Error al confirmar eliminación.' }))
